import { useState } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import {
  ShieldExclamationIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  FireIcon,
  GlobeAltIcon,
  NoSymbolIcon,
  MapPinIcon,
  ChevronRightIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

// Time range display labels
const TIME_RANGE_LABELS = {
  '1h': 'Last 1 hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  'custom': 'Custom range',
};

// Threat level configuration
const threatLevelConfig = {
  safe: { color: '#10B981', bgColor: 'bg-green-100', textColor: 'text-green-700', label: 'Safe' },
  low: { color: '#F59E0B', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', label: 'Low' },
  medium: { color: '#F97316', bgColor: 'bg-orange-100', textColor: 'text-orange-700', label: 'Medium' },
  high: { color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-700', label: 'High' },
  critical: { color: '#DC2626', bgColor: 'bg-red-200', textColor: 'text-red-900', label: 'Critical' },
};

// Progress ring component
function ThreatMeter({ score, size = 120 }) {
  const radius = (size - 16) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;

  const getColor = () => {
    if (score >= 80) return '#DC2626';
    if (score >= 60) return '#EF4444';
    if (score >= 40) return '#F97316';
    if (score >= 20) return '#F59E0B';
    return '#10B981';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="8"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color: getColor() }}>
          {score}
        </span>
        <span className="text-xs text-gray-500">Risk Score</span>
      </div>
    </div>
  );
}

// High-risk IP list item
function HighRiskIPItem({ ip, data, onSelect }) {
  const riskScore = data?.riskScore || 0;
  const country = data?.country || 'Unknown';

  return (
    <button
      onClick={() => onSelect(ip)}
      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-red-50 rounded-lg transition-colors group"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
          <GlobeAltIcon className="w-4 h-4 text-red-600" />
        </div>
        <div className="text-left">
          <p className="font-mono text-sm text-gray-900">{ip}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <MapPinIcon className="w-3 h-3" />
            {country}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-bold ${riskScore >= 60 ? 'text-red-600' : 'text-orange-600'}`}>
          {riskScore}
        </span>
        <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
      </div>
    </button>
  );
}

// Security flag badge
function SecurityFlag({ flag, count }) {
  const flagIcons = {
    'failed_login': ExclamationTriangleIcon,
    'brute_force': FireIcon,
    'invalid_user': NoSymbolIcon,
    'ip_banned': NoSymbolIcon,
    'vpn_detected': GlobeAltIcon,
    'tor_detected': GlobeAltIcon,
    'rate_limited': ExclamationTriangleIcon,
  };

  const Icon = flagIcons[flag] || ExclamationTriangleIcon;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
      <Icon className="w-4 h-4 text-red-600" />
      <span className="text-sm text-red-700 capitalize">{flag.replace(/_/g, ' ')}</span>
      <span className="text-xs font-bold text-red-900 bg-red-100 px-1.5 py-0.5 rounded">
        {count}
      </span>
    </div>
  );
}

export default function SecurityThreatPanel({
  securitySummary,
  onIPSelect,
  onViewAll,
  timeRange = '24h',
  loading = false
}) {
  const [activeTab, setActiveTab] = useState('overview');

  // Get display label for time range
  const getTimeLabel = () => {
    return TIME_RANGE_LABELS[timeRange] || `Last ${timeRange}`;
  };

  if (!securitySummary && !loading) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center">
            <ShieldCheckIcon className="w-6 h-6 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Security Threat Analysis</h3>
            <p className="text-sm text-gray-500">{getTimeLabel()}</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12 text-gray-400">
          <div className="text-center">
            <ShieldCheckIcon className="w-16 h-16 mx-auto mb-3 text-gray-300" />
            <p>No security events in {getTimeLabel().toLowerCase()}</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center animate-pulse">
              <ShieldExclamationIcon className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <div className="h-5 w-48 bg-gray-200 rounded animate-pulse mb-1" />
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-white/50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const threatLevelData = Object.entries(securitySummary.byThreatLevel || {})
    .filter(([, count]) => count > 0)
    .map(([level, count]) => ({
      name: threatLevelConfig[level]?.label || level,
      value: count,
      color: threatLevelConfig[level]?.color || '#6B7280',
    }));

  // Calculate overall risk score
  const calculateOverallRisk = () => {
    const weights = { safe: 0, low: 20, medium: 50, high: 80, critical: 100 };
    const levels = securitySummary.byThreatLevel || {};
    let totalWeight = 0;
    let totalCount = 0;

    Object.entries(levels).forEach(([level, count]) => {
      totalWeight += (weights[level] || 0) * count;
      totalCount += count;
    });

    return totalCount > 0 ? Math.round(totalWeight / totalCount) : 0;
  };

  const overallRisk = calculateOverallRisk();

  return (
    <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-xl border border-red-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-red-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <ShieldExclamationIcon className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Security Threat Analysis</h3>
              <p className="text-sm text-gray-500">{getTimeLabel()}</p>
            </div>
          </div>
          <button
            onClick={onViewAll}
            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5 bg-white/80 hover:bg-white rounded-lg border border-red-200 transition-colors"
          >
            <EyeIcon className="w-4 h-4" />
            View All
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mt-4">
          {['overview', 'threats', 'ips'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab ? 'bg-white shadow-sm text-red-600' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {tab === 'overview' ? 'Overview' : tab === 'threats' ? 'Threat Levels' : 'High-Risk IPs'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Risk Meter */}
            <div className="flex flex-col items-center justify-center bg-white rounded-xl p-6 shadow-sm">
              <ThreatMeter score={overallRisk} size={140} />
              <p className="mt-3 text-sm text-gray-600">Overall Risk Level</p>
            </div>

            {/* Quick Stats */}
            <div className="space-y-3">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Events</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {securitySummary.totalEvents?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">High-Risk IPs</span>
                  <span className="text-2xl font-bold text-red-600">
                    {securitySummary.highRiskIPs?.length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Banned IPs</span>
                  <span className="text-2xl font-bold text-red-900">
                    {securitySummary.bannedIPs?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Threat Distribution Pie */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-sm font-medium text-gray-700 mb-2">Threat Distribution</p>
              {threatLevelData.length > 0 ? (
                <>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={threatLevelData}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={60}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {threatLevelData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center mt-2">
                    {threatLevelData.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-1 text-xs">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-gray-600">{entry.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
                  No threat data
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'threats' && (
          <div>
            {/* Threat Level Bars */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
              <p className="text-sm font-medium text-gray-700 mb-4">Events by Threat Level</p>
              <div className="space-y-3">
                {Object.entries(threatLevelConfig).map(([level, config]) => {
                  const count = securitySummary.byThreatLevel?.[level] || 0;
                  const maxCount = Math.max(...Object.values(securitySummary.byThreatLevel || {}), 1);
                  const percentage = (count / maxCount) * 100;

                  return (
                    <div key={level} className="flex items-center gap-3">
                      <span className={`text-xs font-medium w-16 ${config.textColor}`}>
                        {config.label}
                      </span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%`, backgroundColor: config.color }}
                        />
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-12 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Security Flags */}
            {securitySummary.topFlags && Object.keys(securitySummary.topFlags).length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-sm font-medium text-gray-700 mb-3">Security Flags Detected</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(securitySummary.topFlags).map(([flag, count]) => (
                    <SecurityFlag key={flag} flag={flag} count={count} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ips' && (
          <div>
            {securitySummary.highRiskIPs && securitySummary.highRiskIPs.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {securitySummary.highRiskIPs.slice(0, 15).map((ipData, idx) => {
                  const ip = typeof ipData === 'string' ? ipData : ipData.ip;
                  return (
                    <HighRiskIPItem
                      key={ip || idx}
                      ip={ip}
                      data={typeof ipData === 'object' ? ipData : null}
                      onSelect={onIPSelect}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <ShieldCheckIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-600">No high-risk IPs detected</p>
                <p className="text-sm text-gray-400">All traffic appears normal</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
